import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Smartphone, Share, PlusSquare, X, Download } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("s3d_install_dismissed") === "1") setHidden(true);
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (hidden || isStandalone()) return null;

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setHidden(true);
  };
  const dismiss = () => { localStorage.setItem("s3d_install_dismissed", "1"); setHidden(true); };

  const ios = isIOS();

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-6">
      <motion.div
        data-testid="install-prompt"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden p-8 sm:p-10 rounded-sm border border-neon-cyan/40 bg-card"
        style={{ boxShadow: "0 0 30px rgba(0,240,255,0.1)" }}
      >
        <button data-testid="install-dismiss" onClick={dismiss} aria-label="Chiudi"
          className="absolute top-3 right-3 text-muted-foreground hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="w-14 h-14 rounded-sm flex items-center justify-center border border-neon-cyan/50 bg-neon-cyan/10 shrink-0">
            <Smartphone className="w-7 h-7 text-neon-cyan" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-black text-2xl tracking-tight text-white">Installa l'app sul telefono</h3>
            <p className="text-muted-foreground font-light mt-1">
              Salva <b className="text-neon-cyan">AC Creator 3D</b> nella schermata Home del tuo cellulare e aprilo con un tocco, proprio come una vera app — senza App Store.
            </p>

            {ios ? (
              <div data-testid="install-ios-steps" className="mt-4 flex flex-col gap-2 text-sm text-muted-foreground font-light">
                <span className="inline-flex items-center gap-2">
                  <span className="font-mono text-neon-cyan">1.</span> Tocca <Share className="w-4 h-4 text-neon-cyan inline" /> <b className="text-white">Condividi</b> nella barra di Safari
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="font-mono text-neon-cyan">2.</span> Scegli <PlusSquare className="w-4 h-4 text-neon-cyan inline" /> <b className="text-white">Aggiungi a Home</b>
                </span>
              </div>
            ) : deferred ? (
              <button data-testid="install-android-btn" onClick={install}
                className="mt-5 inline-flex items-center gap-2 h-11 px-6 bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold transition-colors duration-200">
                <Download className="w-4 h-4" /> Installa l'app
              </button>
            ) : (
              <div data-testid="install-generic-steps" className="mt-4 text-sm text-muted-foreground font-light">
                Dal menu del browser (⋮) scegli <b className="text-white">"Installa app"</b> o <b className="text-white">"Aggiungi alla schermata Home"</b>.
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
